import { useState, type ReactNode } from 'react';
import {
  Button,
  Chip,
  Field,
  FilterBar,
  Icon,
  ListItem,
  Screen,
  ScreenHeader,
  Select,
  StateBox,
  StatTile,
  SummaryTiles,
  Workspace,
  WorkspacePane,
  humanize,
} from '@garnet/ui';
import type {
  Assignee,
  Professional as Pro,
  ProRequest,
  Supplier,
  VerticalData,
} from '../data/types.js';
import { COUNTRY, proCost } from '../data/shape.js';
import type { Route } from '../nav.js';

/* Garnet · Professional requests + Professional desk.
   Operator view: raise & track regulated acts (notary, trust lawyer, translator,
   industry assessor) — independently or from the diligence flow.
   Professional desk: the assignee's own dashboard of acts to deliver. */

type ProStatus = ProRequest['status'];

const PST: Record<ProStatus, [string, string]> = {
  in_progress: ['a', 'in progress'],
  awaiting_info: ['a', 'awaiting info'],
  needs_translation: ['a', 'needs translation'],
  delivered: ['m', 'delivered'],
  refused: ['f', 'refused'],
};
function PStat({ s }: { s: ProStatus }) {
  const m = PST[s] ?? ['n', s];
  return <span className={'pill ' + m[0]}>{m[1]}</span>;
}

const TODAY = new Date('2026-06-12');
const daysTo = (d: string): number => Math.round((new Date(d).getTime() - TODAY.getTime()) / 86400000);
const isOpen = (r: ProRequest): boolean => r.status !== 'delivered' && r.status !== 'refused';

type ProPatch = Partial<ProRequest>;
type ActFn = (id: string, patch: ProPatch, text: string) => void;
type DetailTab = 'info' | 'refuse' | 'findings' | null;
type Outcome = 'confirmed' | 'inconclusive' | 'refuted';

/* ── tracked-request detail + professional-side actions ─────────────── */
function Detail({
  r,
  onAct,
  assignees,
}: {
  r: ProRequest;
  onAct: ActFn;
  assignees: Record<string, Assignee[]>;
}) {
  const [tab, setTab] = useState<DetailTab>(null);
  const [info, setInfo] = useState('');
  const [refuse, setRefuse] = useState('');
  const [outcome, setOutcome] = useState<Outcome>('confirmed');
  const [notes, setNotes] = useState('');
  const terminal = r.status === 'delivered' || r.status === 'refused';
  const close = () => {
    setTab(null);
    setInfo('');
    setRefuse('');
    setNotes('');
  };
  const assignInfo = (assignees[r.kind] ?? [])[0];

  const meta = (k: string, v: ReactNode) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 12,
        padding: '9px 0',
        borderBottom: '1px solid var(--rule)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          letterSpacing: '.05em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        {k}
      </span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{v}</span>
    </div>
  );

  return (
    <div style={{ padding: '22px 24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
            {r.id} · {r.record}
          </div>
          <h2
            className="display"
            style={{ fontSize: '1.35rem', fontWeight: 600, margin: '5px 0 4px' }}
          >
            {r.label} · {COUNTRY[r.country]}
          </h2>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{r.supplier}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <PStat s={r.status} />
          <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600, marginTop: 8 }}>
            €{r.cost}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16, borderTop: '1px solid var(--ink)' }}>
        {meta('document', r.document)}
        {meta('assigned', r.assignee + ' · ' + COUNTRY[r.country])}
        {assignInfo && assignInfo.specialties && meta('specialties', assignInfo.specialties.join(' · '))}
        {meta(
          'deadline',
          r.deadline +
            ' · ' +
            (daysTo(r.deadline) < 0
              ? Math.abs(daysTo(r.deadline)) + 'd overdue'
              : 'in ' + daysTo(r.deadline) + 'd'),
        )}
      </div>

      {/* outcome / refusal banners */}
      {r.status === 'delivered' && (
        <div
          className="weak"
          style={{
            marginTop: 16,
            borderLeftColor: 'var(--verified)',
            background: 'var(--verified-bg)',
          }}
        >
          <span className="wl" style={{ color: 'var(--verified-ink)' }}>
            findings
          </span>
          <span className="wt">
            <b>{humanize(r.outcome ?? 'confirmed')}</b>
            {r.findings ? ' · ' + r.findings : ''}. Resolves the dependent check to verified ·
            attestation.
          </span>
        </div>
      )}
      {r.status === 'refused' && (
        <div className="weak brick" style={{ marginTop: 16 }}>
          <span className="wl">refused</span>
          <span className="wt">{r.refusal}</span>
        </div>
      )}

      {/* professional-side actions */}
      {!terminal && (
        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--rule)' }}>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              marginBottom: 10,
            }}
          >
            Acting as {r.assignee}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              sm
              variant="secondary"
              icon="link"
              disabled={r.status === 'needs_translation'}
              onClick={() =>
                onAct(
                  r.id,
                  { status: 'needs_translation' },
                  'Requested a certified translation before certification',
                )
              }
            >
              {r.status === 'needs_translation' ? 'Translation requested' : 'Request translation'}
            </Button>
            <Button
              sm
              variant="secondary"
              icon="file"
              onClick={() => setTab(tab === 'info' ? null : 'info')}
            >
              Request info / documents
            </Button>
            <Button
              sm
              variant="secondary"
              icon="check"
              onClick={() => setTab(tab === 'findings' ? null : 'findings')}
            >
              Submit findings
            </Button>
            <Button sm variant="ghost" icon="x" onClick={() => setTab(tab === 'refuse' ? null : 'refuse')}>
              Refuse
            </Button>
          </div>

          {tab === 'info' && (
            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <Field label="Information or documents required">
                <textarea
                  className="input"
                  rows={2}
                  value={info}
                  onChange={(e) => setInfo(e.target.value)}
                  placeholder="e.g. certified shareholder register, apostilled power of attorney"
                  style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
                />
              </Field>
              <div>
                <Button
                  sm
                  variant="primary"
                  icon="arrow"
                  disabled={info.trim().length < 4}
                  onClick={() => {
                    onAct(
                      r.id,
                      { status: 'awaiting_info' },
                      'Requested additional information · ' + info.trim(),
                    );
                    close();
                  }}
                >
                  Send request to supplier
                </Button>
              </div>
            </div>
          )}
          {tab === 'findings' && (
            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <Field label="Outcome">
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['confirmed', 'inconclusive', 'refuted'] as const).map((o) => (
                    <button
                      key={o}
                      onClick={() => setOutcome(o)}
                      aria-pressed={outcome === o}
                      style={{
                        flex: 1,
                        fontFamily: 'var(--mono)',
                        fontSize: 11.5,
                        fontWeight: 600,
                        padding: '7px 0',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        border: '1px solid ' + (outcome === o ? 'var(--ink)' : 'var(--rule)'),
                        background: outcome === o ? 'var(--ink)' : 'var(--panel)',
                        color: outcome === o ? 'var(--paper)' : 'var(--muted)',
                      }}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Findings note">
                <textarea
                  className="input"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What the professional verified and on what basis."
                  style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
                />
              </Field>
              <div>
                <Button
                  sm
                  variant="primary"
                  icon="check"
                  onClick={() => {
                    onAct(
                      r.id,
                      { status: 'delivered', outcome, findings: notes.trim() },
                      'Findings submitted · ' + outcome + (notes.trim() ? ' · ' + notes.trim() : ''),
                    );
                    close();
                  }}
                >
                  Submit signed findings
                </Button>
              </div>
            </div>
          )}
          {tab === 'refuse' && (
            <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
              <Field label="Reason for refusal">
                <textarea
                  className="input"
                  rows={2}
                  value={refuse}
                  onChange={(e) => setRefuse(e.target.value)}
                  placeholder="Why certification cannot be given."
                  style={{ resize: 'vertical', fontFamily: 'var(--body)' }}
                />
              </Field>
              <div>
                <Button
                  sm
                  variant="primary"
                  icon="x"
                  disabled={refuse.trim().length < 4}
                  onClick={() => {
                    onAct(
                      r.id,
                      { status: 'refused', refusal: refuse.trim() },
                      'Refused certification · ' + refuse.trim(),
                    );
                    close();
                  }}
                >
                  Refuse certification
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* tracking log */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--rule)' }}>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
            marginBottom: 10,
          }}
        >
          Tracking
        </div>
        {[...r.log].reverse().map((l, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 11,
              alignItems: 'flex-start',
              padding: '8px 0',
              borderBottom: i < r.log.length - 1 ? '1px solid var(--rule)' : 0,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: i === 0 ? 'var(--ink)' : 'var(--rule)',
                marginTop: 6,
                flex: 'none',
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5 }}>{l.t}</div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  color: 'var(--muted)',
                  marginTop: 2,
                }}
              >
                {l.who} · {l.at}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── shared list row ─────────────── */
function ReqRow({
  x,
  active,
  onSelect,
}: {
  x: ProRequest;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <ListItem active={active} onSelect={onSelect}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}
      >
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
          {x.id} · {x.country}
        </span>
        <PStat s={x.status} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
        <span style={{ fontSize: 13.5, fontWeight: 500 }}>{x.label}</span>
        {x.kind === 'assessor' && (
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: 'var(--attest-ink)',
              background: 'var(--attest-bg)',
              borderRadius: 2,
              padding: '1px 5px',
            }}
          >
            assessment
          </span>
        )}
        {x.record === 'independent' && (
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              border: '1px solid var(--rule)',
              borderRadius: 2,
              padding: '1px 5px',
            }}
          >
            ad-hoc
          </span>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 3,
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--muted)' }}>{x.supplier}</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, fontWeight: 600 }}>€{x.cost}</span>
      </div>
    </ListItem>
  );
}

/* ── standalone raise-a-request form ─────────────── */
let PRQ_SEQ = 300;
function RaiseRequest({
  professionals,
  assignees,
  suppliers,
  onCreate,
  onClose,
}: {
  professionals: Pro[];
  assignees: Record<string, Assignee[]>;
  suppliers: Supplier[];
  onCreate: (req: ProRequest) => void;
  onClose: () => void;
}) {
  const first = professionals[0];
  const [kind, setKind] = useState<string>(first?.kind ?? '');
  const pro = professionals.find((p) => p.kind === kind);
  const [country, setCountry] = useState<string>(pro?.jurisdictions[0] ?? first?.jurisdictions[0] ?? '');
  const [supplier, setSupplier] = useState('');
  const [doc, setDoc] = useState('');
  const [deadline, setDeadline] = useState('2026-06-19');

  if (!pro) return null;

  const assignee = (assignees[kind] ?? [])[0];
  const cost = proCost(pro.cost, country);
  const ok = doc.trim().length > 3;

  function pickKind(k: string) {
    const p = professionals.find((x) => x.kind === k);
    setKind(k);
    setCountry(p?.jurisdictions[0] ?? country);
  }

  function submit() {
    if (!pro) return;
    PRQ_SEQ += 1;
    onCreate({
      id: 'PRQ-' + PRQ_SEQ,
      kind,
      label: pro.label,
      country,
      supplier: supplier || 'Ad-hoc · no supplier',
      record: 'independent',
      document: doc.trim(),
      deadline,
      status: 'in_progress',
      cost,
      assignee: assignee ? assignee.firm : 'Garnet network',
      log: [{ at: 'now', who: 'operator', t: 'Request raised independently · ' + doc.trim() }],
    });
  }

  return (
    <div
      className="panel"
      style={{ padding: '20px 22px', marginBottom: 18, borderColor: 'var(--ink-2)' }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 10.5,
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
          }}
        >
          Raise a request · independent of any diligence
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            border: '1px solid var(--rule)',
            background: 'transparent',
            borderRadius: 'var(--radius-md)',
            width: 28,
            height: 28,
            cursor: 'pointer',
            color: 'var(--muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="x" size={15} />
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Professional act">
          <Select
            value={kind}
            onChange={pickKind}
            ariaLabel="Professional act"
            options={professionals.map((p) => ({ value: p.kind, label: p.label }))}
          />
        </Field>
        <Field label="Country · in-country network">
          <Select
            value={country}
            onChange={setCountry}
            ariaLabel="Country"
            options={pro.jurisdictions.map((c) => ({ value: c, label: c + ' · ' + COUNTRY[c] }))}
          />
        </Field>
      </div>

      <div
        style={{
          fontSize: 12.5,
          color: 'var(--muted)',
          lineHeight: 1.5,
          margin: '10px 0 4px',
          maxWidth: '72ch',
        }}
      >
        {pro.desc}
        {pro.industry ? ' · ' + pro.industry + ' specialist' : ''}.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 10 }}>
        <Field label="Supplier · optional">
          <Select
            value={supplier}
            onChange={setSupplier}
            ariaLabel="Supplier"
            options={[
              { value: '', label: '— Ad-hoc · no supplier —' },
              ...suppliers.map((s) => ({ value: s.name, label: s.name })),
            ]}
          />
        </Field>
        <Field label="Deadline">
          <input
            className="input"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </Field>
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Document / scope of the act">
          <input
            className="input"
            value={doc}
            onChange={(e) => setDoc(e.target.value)}
            placeholder={
              kind === 'assessor'
                ? 'e.g. UNECE R90 type-approval review · brake disc'
                : 'e.g. notarise signatory authority on the board resolution'
            }
          />
        </Field>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 16,
          paddingTop: 14,
          borderTop: '1px solid var(--rule-soft)',
          flexWrap: 'wrap',
        }}
      >
        {assignee && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
            routed to{' '}
            <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{assignee.firm}</b> · {assignee.place} ·
            SLA {assignee.sla}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600 }}>€{cost}</span>
        <Button sm variant="primary" icon="plus" disabled={!ok} onClick={submit}>
          Raise request
        </Button>
        <Button sm variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function FilterChips({
  filter,
  setFilter,
  extra,
}: {
  filter: string;
  setFilter: (v: string) => void;
  extra?: ReactNode;
}) {
  const chip = (v: string, label: string) => (
    <Chip key={v} active={filter === v} onClick={() => setFilter(v)}>
      {label}
    </Chip>
  );
  return (
    <FilterBar label="filter">
      {chip('all', 'all')}
      {chip('open', 'open')}
      {extra}
      {chip('needs_translation', 'needs translation')}
      {chip('awaiting_info', 'awaiting info')}
      {chip('delivered', 'delivered')}
      {chip('refused', 'refused')}
    </FilterBar>
  );
}

function applyFilter(reqs: ProRequest[], filter: string): ProRequest[] {
  return reqs.filter((r) =>
    filter === 'all'
      ? true
      : filter === 'open'
        ? isOpen(r)
        : filter === 'in_progress'
          ? r.status === 'in_progress'
          : r.status === filter,
  );
}

/* ── operator view ─────────────── */
export function Professional({ data }: { data: VerticalData; go: (r: Route) => void }) {
  const [reqs, setReqs] = useState<ProRequest[]>(data.proRequests);
  const [sel, setSel] = useState<string>(data.proRequests[0]?.id ?? '');
  const [filter, setFilter] = useState('all');
  const [raising, setRaising] = useState(false);

  const onAct: ActFn = (id, patch, text) =>
    setReqs((rs) =>
      rs.map((r) =>
        r.id === id
          ? { ...r, ...patch, log: [...r.log, { at: 'now', who: r.kind, t: text }] }
          : r,
      ),
    );

  function createReq(req: ProRequest) {
    setReqs((rs) => [req, ...rs]);
    setSel(req.id);
    setRaising(false);
  }

  const list = applyFilter(reqs, filter);
  const r = reqs.find((x) => x.id === sel) ?? list[0];

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Professional requests"
        title="Regulated acts & their tracking"
        lead="Raise a professional act — notary, trust lawyer, certified translator, or an industry assessor — on its own or from the diligence flow. The assigned professional can ask for a translation or further documents, refuse certification, or submit signed findings; every step is logged, and a completed act resolves to gold."
        actions={
          !raising && (
            <Button variant="primary" icon="plus" onClick={() => setRaising(true)}>
              Raise a request
            </Button>
          )
        }
      />

      {raising && (
        <RaiseRequest
          professionals={data.professionals}
          assignees={data.assignees}
          suppliers={data.suppliers}
          onCreate={createReq}
          onClose={() => setRaising(false)}
        />
      )}

      <FilterChips filter={filter} setFilter={setFilter} />

      <Workspace rail={340}>
        <WorkspacePane>
          <div className="pane-sticky" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 14px' }}>
            Requests · {list.length}
          </div>
          {list.length === 0 ? (
            <div style={{ padding: 20 }}>
              <StateBox kind="empty" title="None in this filter" />
            </div>
          ) : (
            <div className="list">
              {list.map((x) => (
                <ReqRow key={x.id} x={x} active={!!r && x.id === r.id} onSelect={() => setSel(x.id)} />
              ))}
            </div>
          )}
        </WorkspacePane>
        {r ? (
          <WorkspacePane>
            <Detail r={r} onAct={onAct} assignees={data.assignees} />
          </WorkspacePane>
        ) : (
          <WorkspacePane flush>
            <StateBox kind="empty" title="Select a request" />
          </WorkspacePane>
        )}
      </Workspace>
    </Screen>
  );
}

/* ── professional desk · the assignee's own dashboard ─────────────── */
export function ProfessionalDesk({ data }: { data: VerticalData }) {
  const [reqs, setReqs] = useState<ProRequest[]>(data.proRequests);
  const [sel, setSel] = useState<string>(
    () => (data.proRequests.find((r) => r.status === 'in_progress') ?? data.proRequests[0])?.id ?? '',
  );
  const [filter, setFilter] = useState('open');

  const onAct: ActFn = (id, patch, text) =>
    setReqs((rs) =>
      rs.map((r) =>
        r.id === id
          ? { ...r, ...patch, log: [...r.log, { at: 'now', who: r.kind, t: text }] }
          : r,
      ),
    );

  const open = reqs.filter(isOpen);
  const dueSoon = open.filter((r) => daysTo(r.deadline) <= 3).length;
  const actionNeeded = reqs.filter((r) => r.status === 'in_progress').length;
  const fees = open.reduce((s, r) => s + r.cost, 0);
  const firms = [...new Set(reqs.map((r) => r.assignee))];

  const list = applyFilter(reqs, filter);
  const r = reqs.find((x) => x.id === sel) ?? list[0];

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Professional desk"
        title="Acts to deliver"
        lead={`The professional's own queue. Acts routed through Garnet's in-country network — ${firms.slice(0, 3).join(' · ')} — with deadlines, fees, and the document to assess. Deliver findings, request a translation or further documents, or refuse.`}
      />

      <SummaryTiles min={150}>
        <StatTile label="Assigned · open" value={open.length} sub={'of ' + reqs.length} />
        <StatTile
          label="Action needed"
          value={actionNeeded}
          sub="awaiting you"
          valueStyle={actionNeeded ? { color: 'var(--attest-ink)' } : undefined}
        />
        <StatTile label="Due ≤ 3 days" value={dueSoon} valueStyle={dueSoon ? { color: 'var(--flag)' } : undefined} />
        <StatTile label="Open fees" value={'€' + fees.toLocaleString()} />
      </SummaryTiles>

      <FilterChips
        filter={filter}
        setFilter={setFilter}
        extra={
          <Chip active={filter === 'in_progress'} onClick={() => setFilter('in_progress')}>
            action needed
          </Chip>
        }
      />

      <Workspace rail={340}>
        <WorkspacePane>
          <div className="pane-sticky" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)', padding: '12px 14px' }}>
            My acts · {list.length}
          </div>
          {list.length === 0 ? (
            <div style={{ padding: 20 }}>
              <StateBox kind="empty" title="None in this filter" />
            </div>
          ) : (
            <div className="list">
              {list.map((x) => (
                <ReqRow key={x.id} x={x} active={!!r && x.id === r.id} onSelect={() => setSel(x.id)} />
              ))}
            </div>
          )}
        </WorkspacePane>
        {r ? (
          <WorkspacePane>
            <Detail r={r} onAct={onAct} assignees={data.assignees} />
          </WorkspacePane>
        ) : (
          <WorkspacePane flush>
            <StateBox kind="empty" title="Select an act" />
          </WorkspacePane>
        )}
      </Workspace>
    </Screen>
  );
}
